import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  AlertTriangle,
  Check,
  Copy,
  ExternalLink,
  Link2,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import {
  createSupportLink,
  deleteSupportLink,
  getSupportLinks,
  shareableLink,
  updateSupportLink,
} from '../../Services/SupportService';
import {
  SUPPORT_CHANNEL_LABELS,
  SUPPORT_CHANNELS,
  SupportChannelKey,
  SupportLink,
} from '../../Types/SupportType';
import { Alert, AlertProps } from '../../../Shared/Utils/Alert';
import { formatDateTime } from '../../../Shared/Utils/HelperUtils';
import GrowthDateFilter from '../Growth/GrowthDateFilter';
import SupportTabNav from './SupportTabNav';

const headerClass = 'px-5 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500';
const inputClass =
  'w-full rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2.5 text-sm font-semibold text-slate-100 outline-none transition placeholder:text-slate-600 focus:border-cyan-500/50';
const labelClass = 'mb-1 block text-[10px] font-black uppercase tracking-widest text-slate-400';

const emptyForm = {
  displayName: '',
  channel: 'linkedin' as SupportChannelKey,
  baseUrl: '',
  /** Empty means "use the channel". Kept separate so switching channel does not clobber a deliberate override. */
  utmSource: '',
  utmMedium: 'social',
  utmCampaign: '',
  utmContent: '',
  messageTemplate: '',
  active: true,
};

/**
 * Builds the URL locally for the live preview.
 *
 * <p>The backend assembles the authoritative URL; this mirrors it only so the admin sees the
 * link while typing instead of after saving. Query parameters already on the base URL are
 * preserved, matching `SupportLinkRequestDTO.resolveUri`, so the preview cannot show a different
 * destination from the one that ends up stored.</p>
 */
/**
 * URL/search-safe slug, matching `SupportServiceImpl.slug` on the backend.
 *
 * <p>This must stay byte-for-byte equivalent to the server's version. The two are the same
 * decision reached in two places, and if they ever disagree the admin sees one URL in the
 * preview and a different one gets stored - the analytics then split across two campaign keys
 * and neither row reports the full traffic. Hence the explicit mirroring rather than a
 * "close enough" local implementation.</p>
 */
function slugUtmValue(value: string): string {
  const slug = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  if (!slug) return '';
  return slug.length > 100 ? slug.slice(0, 100) : slug;
}

function previewUrl(
  baseUrl: string,
  utmSource: string,
  utmMedium: string,
  utmCampaign: string,
  utmContent: string
): string {
  if (!baseUrl.trim()) return '';
  try {
    const base = new URL(baseUrl.trim());
    const utm: [string, string][] = [
      ['utm_source', utmSource],
      ['utm_medium', utmMedium],
      ['utm_campaign', utmCampaign],
      ['utm_content', utmContent],
    ];
    utm.forEach(([key, value]) => {
      if (value && value.trim()) base.searchParams.set(key, value.trim());
    });
    return base.toString();
  } catch {
    return '';
  }
}

function SupportLinkGenerator() {
  const [alert, setAlert] = useState<{ variant: AlertProps['variant']; title: string; message: string } | null>(null);
  const [links, setLinks] = useState<SupportLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<SupportLink | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [copied, setCopied] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  // Listing filters. Kept separate from the form so filtering never disturbs a half-written link.
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [appliedFrom, setAppliedFrom] = useState('');
  const [appliedTo, setAppliedTo] = useState('');
  const [channelFilter, setChannelFilter] = useState<SupportChannelKey | ''>('');
  const [activeFilter, setActiveFilter] = useState<'' | 'true' | 'false'>('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const showAlert = (variant: AlertProps['variant'], title: string, message: string) =>
    setAlert({ variant, title, message });

  const fetchLinks = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getSupportLinks({
        channel: channelFilter || undefined,
        active: activeFilter === '' ? undefined : activeFilter === 'true',
        search: search || undefined,
        fromDate: appliedFrom || undefined,
        toDate: appliedTo || undefined,
      });
      if (response.success && Array.isArray(response.data)) {
        setLinks(response.data);
      }
    } catch (err) {
      console.error('Failed to load support links', err);
      showAlert('error', 'Load error', 'Could not load tracking links.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelFilter, activeFilter, search, appliedFrom, appliedTo]);

  useEffect(() => {
    fetchLinks();
  }, [fetchLinks]);

  /*
   * Selecting a channel pre-fills the UTM source, because the source is what the tab groups on
   * and having to retype `linkedin` to file a link under the LinkedIn tab is pure friction. The
   * field stays editable for the case where an admin is deliberately attributing a WhatsApp post
   * to something else.
   */
  const effectiveSource = form.utmSource || form.channel;

  /*
   * The campaign and content fallbacks are derived once and reused by both the preview and the
   * payload. They used to be computed twice, which is how the preview drifted from what was
   * stored: the preview fell back to the raw display name while the server would have slugged it,
   * so "Spring Careers 2026" could be shown one way and stored another. One derivation, two
   * consumers - they cannot disagree now.
   */
  const effectiveCampaign = form.utmCampaign.trim() || slugUtmValue(form.displayName);
  const effectiveContent = form.utmContent.trim() || slugUtmValue(form.displayName);

  const preview = useMemo(
    () =>
      previewUrl(
        form.baseUrl,
        effectiveSource,
        form.utmMedium,
        effectiveCampaign,
        effectiveContent
      ),
    [form.baseUrl, effectiveSource, form.utmMedium, effectiveCampaign, effectiveContent]
  );

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (link: SupportLink) => {
    setEditing(link);
    setForm({
      displayName: link.displayName,
      channel: (link.channel ?? 'linkedin') as SupportChannelKey,
      baseUrl: link.baseUrl,
      // Back to empty when it matches the channel, so the preview does not show a literal
      // "linkedin" that the admin did not type.
      utmSource: link.utmSource === link.channel ? '' : (link.utmSource ?? ''),
      utmMedium: link.utmMedium ?? '',
      utmCampaign: link.utmCampaign ?? '',
      utmContent: link.utmContent ?? '',
      messageTemplate: link.messageTemplate ?? '',
      active: link.active,
    });
    setShowForm(true);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        displayName: form.displayName.trim(),
        channel: form.channel,
        baseUrl: form.baseUrl.trim(),
        utmSource: effectiveSource,
        utmMedium: form.utmMedium.trim() || null,
        utmCampaign: effectiveCampaign || null,
        utmContent: effectiveContent || null,
        messageTemplate: form.messageTemplate.trim() || null,
        active: form.active,
      };

      const response = editing
        ? await updateSupportLink(editing.id, payload)
        : await createSupportLink(payload);

      if (response.success) {
        showAlert(
          'success',
          editing ? 'Link updated' : 'Link created',
          'Saved. Copy the Ready to share value from the table below.'
        );
        setShowForm(false);
        setEditing(null);
        setForm(emptyForm);
        fetchLinks();
      } else {
        showAlert('error', 'Save failed', response.message || 'Please check the fields.');
      }
    } catch {
      showAlert('error', 'Save failed', 'Could not save the link. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    const response = await deleteSupportLink(id);
    if (response.success) {
      showAlert('success', 'Link deleted', 'The link is gone. Its outreach history was kept.');
      fetchLinks();
    } else {
      showAlert('error', 'Delete failed', response.message || 'Please try again.');
    }
    setConfirmDelete(null);
  };

  const copy = async (value: string, key: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      setTimeout(() => setCopied((c) => (c === key ? null : c)), 2000);
    } catch {
      showAlert('error', 'Copy failed', 'Unable to copy to the clipboard.');
    }
  };

  /*
   * No totals row here, deliberately. Per-link analytics match on the campaign+content pair, so
   * two links sharing a pair each already report the combined traffic - summing the column would
   * double-count exactly those rows. The per-link numbers are honest; a column total would not
   * be, and the sharedCampaign warning is what tells the admin why.
   */

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      <SupportTabNav />

      {alert && (
        <Alert
          variant={alert.variant}
          title={alert.title}
          message={alert.message}
          autoCloseMs={6000}
          onClose={() => setAlert(null)}
        />
      )}

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
          <Link2 size={22} />
        </span>
        <div>
          <h1 className="text-xl font-black tracking-tight text-slate-100 md:text-2xl">
            Generate Link
          </h1>
          <p className="text-xs font-semibold text-slate-400 md:text-sm">
            Build a tracked URL for a channel and see what it brings in
          </p>
        </div>
        <div className="ml-auto flex gap-2">
          <button
            onClick={fetchLinks}
            title="Refresh"
            className="inline-flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-4 py-2.5 text-sm font-bold text-slate-200 transition hover:border-cyan-500/40 hover:text-cyan-400"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 to-teal-500 px-4 py-2.5 text-xs font-black uppercase tracking-wider text-slate-950 transition hover:from-cyan-400 hover:to-teal-400"
          >
            <Plus size={15} />
            New Link
          </button>
        </div>
      </div>

      {showForm && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 rounded-2xl border border-cyan-500/30 bg-slate-900/60 p-5"
        >
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-sm font-black text-slate-100">
              {editing ? `Edit "${editing.displayName}"` : 'New tracking link'}
            </h2>
            <button
              onClick={() => {
                setShowForm(false);
                setEditing(null);
              }}
              className="rounded-lg p-1.5 text-slate-500 transition hover:bg-slate-800 hover:text-slate-200"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className={labelClass}>Display name</label>
              <input
                value={form.displayName}
                onChange={(e) => setForm({ ...form, displayName: e.target.value })}
                placeholder="Q3 LinkedIn founders post"
                className={inputClass}
              />
              <p className="mt-1 text-[10px] font-semibold text-slate-600">
                A full URL is shared exactly as typed (e.g. https://nexgent.tech); any other text
                becomes a short link built from it (swapnil → …/l/swapnil). Also labels the link
                in reports and defaults the campaign and content values.
              </p>
            </div>

            <div>
              <label className={labelClass}>Channel</label>
              <select
                value={form.channel}
                onChange={(e) => setForm({ ...form, channel: e.target.value as SupportChannelKey })}
                className={inputClass}
              >
                {SUPPORT_CHANNELS.map((channel) => (
                  <option key={channel} value={channel}>
                    {SUPPORT_CHANNEL_LABELS[channel]}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-[10px] font-semibold text-slate-600">
                Which tab this link is listed under.
              </p>
            </div>

            <div className="md:col-span-2">
              <label className={labelClass}>Destination URL</label>
              <input
                value={form.baseUrl}
                onChange={(e) => setForm({ ...form, baseUrl: e.target.value })}
                placeholder="https://nexgent.com/register"
                className={inputClass}
              />
              <p className="mt-1 text-[10px] font-semibold text-slate-600">
                Any existing query parameters are kept; UTM values are added alongside them.
              </p>
            </div>

            <div>
              <label className={labelClass}>UTM source</label>
              <input
                value={form.utmSource}
                onChange={(e) => setForm({ ...form, utmSource: e.target.value })}
                className={inputClass}
                placeholder={form.channel}
              />
              <p className="mt-1 text-[10px] font-semibold text-slate-600">
                Defaults to the channel. Changing it moves the link to a different analytics group.
              </p>
            </div>

            <div>
              <label className={labelClass}>UTM medium</label>
              <input
                value={form.utmMedium}
                onChange={(e) => setForm({ ...form, utmMedium: e.target.value })}
                placeholder="social"
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>UTM campaign</label>
              <input
                value={form.utmCampaign}
                onChange={(e) => setForm({ ...form, utmCampaign: e.target.value })}
                placeholder={form.displayName || 'from display name'}
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>UTM content</label>
              <input
                value={form.utmContent}
                onChange={(e) => setForm({ ...form, utmContent: e.target.value })}
                placeholder={form.displayName || 'from display name'}
                className={inputClass}
              />
            </div>

            <div className="md:col-span-2">
              <label className={labelClass}>Message template (optional)</label>
              <textarea
                value={form.messageTemplate}
                onChange={(e) => setForm({ ...form, messageTemplate: e.target.value })}
                rows={3}
                placeholder="Hi - you visited Nexgent but never finished signing up. Here is the link: {link}"
                className={`${inputClass} resize-y`}
              />
              <p className="mt-1 text-[10px] font-semibold text-slate-600">
                Used on the channel tabs when nudging a visitor. {'{link}'} is replaced with the display name
                as a hyperlink, e.g. [test](url).
              </p>
            </div>
          </div>

          {preview && (
            <div className="mt-4">
              <label className={labelClass}>Preview</label>
              <div className="flex items-center gap-2">
                <code className="flex-1 overflow-x-auto whitespace-nowrap rounded-xl border border-slate-800 bg-slate-950 px-3 py-2.5 text-xs font-semibold text-cyan-300">
                  {preview}
                </code>
                <button
                  onClick={() => copy(preview, 'preview')}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2.5 text-xs font-black text-slate-300 transition hover:border-cyan-500/40 hover:text-cyan-400"
                >
                  {copied === 'preview' ? <Check size={14} /> : <Copy size={14} />}
                </button>
              </div>
            </div>
          )}

          <div className="mt-5 flex items-center gap-3">
            <button
              onClick={handleSubmit}
              disabled={submitting || !form.displayName.trim() || !form.baseUrl.trim()}
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 to-teal-500 px-5 py-2.5 text-xs font-black uppercase tracking-wider text-slate-950 transition hover:from-cyan-400 hover:to-teal-400 disabled:opacity-40"
            >
              {submitting ? <Loader2 size={15} className="animate-spin" /> : <Link2 size={15} />}
              {editing ? 'Save Changes' : 'Create Link'}
            </button>
            <button
              onClick={() => {
                setShowForm(false);
                setEditing(null);
              }}
              disabled={submitting}
              className="rounded-xl border border-slate-800 bg-slate-950/60 px-4 py-2.5 text-xs font-black uppercase tracking-wider text-slate-300 transition hover:text-slate-100 disabled:opacity-40"
            >
              Cancel
            </button>
            {!form.baseUrl.trim() && (
              <span className="text-[10px] font-semibold text-slate-600">
                A destination URL is required.
              </span>
            )}
          </div>
        </motion.div>
      )}

      <div className="mb-4 grid grid-cols-1 gap-3 xl:grid-cols-2">
        <GrowthDateFilter
          fromDate={fromDate}
          toDate={toDate}
          onApply={(f, t) => {
            setFromDate(f);
            setToDate(t);
            setAppliedFrom(f);
            setAppliedTo(t);
          }}
        />
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setSearch(searchInput.trim());
          }}
          className="flex items-center gap-2"
        >
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Display name or campaign"
            className="w-64 rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2 text-xs font-semibold text-slate-100 outline-none transition placeholder:text-slate-600 focus:border-cyan-500/50"
          />
          <button
            type="submit"
            className="inline-flex items-center gap-2 rounded-xl bg-cyan-500 px-4 py-2 text-xs font-black uppercase tracking-wider text-slate-950 transition hover:bg-cyan-400"
          >
            <Search size={14} />
          </button>
        </form>

        <select
          value={channelFilter}
          onChange={(e) => setChannelFilter(e.target.value as SupportChannelKey | '')}
          className="rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2 text-xs font-semibold text-slate-100 outline-none transition focus:border-cyan-500/50"
        >
          <option value="">All channels</option>
          {SUPPORT_CHANNELS.map((channel) => (
            <option key={channel} value={channel}>
              {SUPPORT_CHANNEL_LABELS[channel]}
            </option>
          ))}
        </select>

        <select
          value={activeFilter}
          onChange={(e) => setActiveFilter(e.target.value as '' | 'true' | 'false')}
          className="rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2 text-xs font-semibold text-slate-100 outline-none transition focus:border-cyan-500/50"
        >
          <option value="">Active &amp; archived</option>
          <option value="true">Active only</option>
          <option value="false">Archived only</option>
        </select>

        {(search || channelFilter || activeFilter) && (
          <button
            onClick={() => {
              setSearch('');
              setSearchInput('');
              setChannelFilter('');
              setActiveFilter('');
            }}
            className="text-xs font-bold text-slate-400 underline transition hover:text-cyan-400"
          >
            Clear filters
          </button>
        )}
      </div>

      {loading && links.length === 0 ? (
        <div className="flex min-h-[30vh] items-center justify-center">
          <RefreshCw className="h-8 w-8 animate-spin text-cyan-500" />
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/40">
          <table className="w-full min-w-[1040px] text-left">
            <thead>
              <tr className="border-b border-slate-800 bg-slate-900/60">
                <th className={headerClass}>Link</th>
                <th className={headerClass}>Channel</th>
                <th className={headerClass}>Visitors</th>
                <th className={headerClass}>Not registered</th>
                <th className={headerClass}>Registered</th>
                <th className={headerClass}>Nudges</th>
                <th className={headerClass}>Created</th>
                <th className={headerClass} />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/70">
              {links.map((link, idx) => {
                const share = shareableLink(link);
                return (
                <motion.tr
                  key={link.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(idx * 0.02, 0.3) }}
                  className={`transition hover:bg-slate-900/70 ${link.active ? '' : 'opacity-55'}`}
                >
                  <td className="max-w-sm px-5 py-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-slate-100">{link.displayName}</span>
                      {!link.active && (
                        <span className="rounded-md border border-slate-700 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider text-slate-500">
                          Archived
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex items-center gap-2">
                      <code className="min-w-0 flex-1 truncate text-[10px] text-cyan-400/80">
                        {link.url}
                      </code>
                      <button
                        onClick={() => link.url && copy(link.url, link.id)}
                        title="Copy URL"
                        className="shrink-0 rounded-md p-1 text-slate-500 transition hover:text-cyan-400"
                      >
                        {copied === link.id ? <Check size={12} /> : <Copy size={12} />}
                      </button>
                    </div>
                    {/*
                      The ready-to-share value sits above the full URL: a display name that is a
                      full URL is shown and copied exactly as typed, anything else resolves to
                      the short link, which redirects through `{site}/l/{slug}` to the tracked
                      URL below so tracking still records the full UTM set. One clean value, no
                      markdown brackets around it.
                    */}
                    {share && (
                      <div className="mt-2 rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-2 py-1.5">
                        <label className="text-[9px] font-black uppercase tracking-widest text-emerald-400">
                          Ready to share
                        </label>
                        <div className="mt-1 flex items-center gap-2">
                          {/*
                            The displayed value stays the share link, but clicking it (or the
                            icon) opens the destination URL from the Preview - the display name
                            maps to that tracked URL, so the admin sees exactly where a click
                            on the shared value is meant to land.
                          */}
                          <a
                            href={link.url || share}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Open destination URL"
                            className="min-w-0 flex-1 truncate text-[11px] font-bold text-emerald-300 underline decoration-dotted underline-offset-2 transition hover:text-emerald-200"
                          >
                            {share}
                          </a>
                          <button
                            onClick={() => copy(share, `${link.id}:short`)}
                            title="Copy share link"
                            className="shrink-0 rounded-md p-1 text-slate-500 transition hover:text-emerald-400"
                          >
                            {copied === `${link.id}:short` ? <Check size={12} /> : <Copy size={12} />}
                          </button>
                          <a
                            href={link.url || share}
                            onClick={(e) => e.stopPropagation()}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Open destination URL"
                            className="shrink-0 rounded-md p-1 text-slate-500 transition hover:text-emerald-400"
                          >
                            <ExternalLink size={12} />
                          </a>
                        </div>
                      </div>
                    )}
                    {link.sharedCampaign && (
                      <p className="mt-1 flex items-center gap-1 text-[10px] font-semibold text-amber-400">
                        <AlertTriangle size={11} />
                        Shares a campaign with another link, so both show the same traffic.
                      </p>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    <span className="rounded-lg border border-slate-800 bg-slate-950 px-2.5 py-1 text-xs font-bold text-slate-300">
                      {link.channel ? SUPPORT_CHANNEL_LABELS[link.channel] : '—'}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-sm font-semibold text-slate-200">
                    {link.visitors.toLocaleString()}
                  </td>
                  <td className="px-5 py-4">
                    <span
                      className={`text-sm font-black tabular-nums ${
                        link.visitors - link.registered > 0 ? 'text-amber-400' : 'text-slate-600'
                      }`}
                    >
                      {(link.visitors - link.registered).toLocaleString()}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <span className="text-sm font-semibold text-slate-300">
                      {link.registered.toLocaleString()}
                    </span>
                    <span className="ml-1.5 text-[10px] font-bold text-cyan-400">
                      {link.conversionPct.toFixed(1)}%
                    </span>
                  </td>
                  <td className="px-5 py-4 text-sm font-semibold text-slate-300">
                    {link.outreachCount.toLocaleString()}
                  </td>
                  <td className="px-5 py-4 text-xs font-semibold text-slate-400">
                    {formatDateTime(link.createdAt)}
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center justify-end gap-1.5">
                      <button
                        onClick={() => openEdit(link)}
                        className="rounded-lg border border-slate-800 bg-slate-950/60 px-2.5 py-1.5 text-[10px] font-black uppercase tracking-wider text-slate-300 transition hover:border-cyan-500/40 hover:text-cyan-400"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => setConfirmDelete(link.id)}
                        title="Delete link"
                        className="rounded-lg border border-slate-800 bg-slate-950/60 p-1.5 text-slate-400 transition hover:border-rose-500/40 hover:text-rose-400"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </motion.tr>
                );
              })}
              {links.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-5 py-16 text-center">
                    <p className="text-sm font-bold text-slate-400">No tracking links yet.</p>
                    <p className="mt-1 text-xs font-semibold text-slate-600">
                      Create one, share it on a channel, and the traffic and registrations land here
                      and in Growth.
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Destructive, so confirmed rather than immediate. The copy states what survives. */}
      {confirmDelete && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 p-6">
            <h3 className="text-base font-black text-slate-100">Delete this link?</h3>
            <p className="mt-2 text-xs font-semibold text-slate-400">
              The URL stops being tracked for new visitors. Its recorded nudges are kept so the
              audit trail survives.
            </p>
            <div className="mt-5 flex gap-3">
              <button
                onClick={() => setConfirmDelete(null)}
                className="flex-1 rounded-xl border border-slate-800 bg-slate-950/60 py-2.5 text-xs font-black uppercase tracking-wider text-slate-300 transition hover:text-slate-100"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(confirmDelete)}
                className="flex-1 rounded-xl bg-rose-500 py-2.5 text-xs font-black uppercase tracking-wider text-slate-950 transition hover:bg-rose-400"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SupportLinkGenerator;
